import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import {
  ProjectsLocationsData,
  UpdateProjectLocationPayload
} from "../../../models/ProjectData";
import DataSvc from "../../../services/dataSvc";

export type ProjectLocationsSortFields = string;

export type ProjectsLocationsFilterFormData = {
  numberPerPage: number;
  pageIndex: number;
};

export type ProjectsLocationsSearchParams = {
  location_name?: string;
  sort: ProjectLocationsSortFields;
  order: "asc" | "desc";
  limit: number;
  offset: number;
  seek_total_count: boolean;
  scores?: string[];
};

export const useProjectsLocations = (
  organizationId: string,
  projectId?: string
) => {
  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(`projectManagementPage.projectFacilitiesList.${key}`);

  const client = useQueryClient();

  const [searchParams, setSearchParams] =
    useState<ProjectsLocationsSearchParams>({
      location_name: "",
      sort: "location.name",
      order: "asc",
      limit: 10,
      offset: 0,
      seek_total_count: true
    });

  // Filter Form Data is used for the legacy pagination component
  const filterFormData = useMemo(() => {
    const NUMBER_PER_PAGE = 10;
    return {
      numberPerPage: NUMBER_PER_PAGE,
      pageIndex: Math.floor(searchParams.offset / NUMBER_PER_PAGE) + 1
    };
  }, [searchParams]);

  // handleChangeFilterFormData is used to change
  // the output from the legacy pagination component into search params
  const handleChangeFilterFormData = (
    filterFormData: ProjectsLocationsFilterFormData
  ) => {
    setSearchParams((sp) => {
      return {
        ...sp,
        offset: (filterFormData.pageIndex - 1) * filterFormData.numberPerPage
      };
    });
  };

  const query = useQuery<{ data: ProjectsLocationsData[]; count: number }>({
    queryKey: ["projectsLocations", organizationId, projectId, searchParams],
    queryFn: () =>
      DataSvc.getProjectsLocations({
        organizationId,
        projectId,
        params: searchParams
      }),
    enabled: !!organizationId && !!projectId
  });

  const updateMutation = useMutation({
    mutationFn: ({
      projectLocationId,
      payload
    }: {
      organizationId: string;
      projectId?: string;
      projectLocationId?: string;
      payload: UpdateProjectLocationPayload;
    }) =>
      DataSvc.updateProjectLocation({
        organizationId,
        projectId,
        projectLocationId,
        payload
      })
  });

  const updateProjectLocation = async ({
    projectLocationId,
    payload
  }: {
    projectLocationId?: string;
    payload: UpdateProjectLocationPayload;
  }) => {
    await updateMutation.mutateAsync({
      organizationId,
      projectId,
      projectLocationId,
      payload
    });
    toast.success(t("update_success"));
    client.setQueryData(
      ["projectsLocations", organizationId, projectId],
      (oldData: ProjectsLocationsData[] | undefined) => {
        if (!oldData) return [];
        return oldData.map((item) =>
          Number(item.id) === Number(projectLocationId)
            ? {
                ...item,
                ...payload
              }
            : item
        );
      }
    );
    client.invalidateQueries({
      queryKey: ["projectsLocations", organizationId, projectId]
    });
  };

  return {
    query,
    updateProjectLocation,
    searchParams,
    setSearchParams,
    filterFormData,
    handleChangeFilterFormData
  };
};
